import axios from 'axios';
import { BASE_URL } from '../api/config';

// Service du menu « Alertes » (règles d'alerte automatique « départ proche »).
// Pas de try/catch ici : l'erreur remonte au composant → toast.

export type ModeDeclenchement = 'fenetre' | 'exact';
export type StatutRegle = 'jamais' | 'vide' | 'envoye' | 'dry_run' | 'echec_template' | 'echec_envoi' | 'erreur';
export type StatutCandidat = 'a_envoyer' | 'deja_alerte' | 'a_venir' | 'depasse' | 'illisible';

export interface AlerteRegleCompteurs {
  nb_concernes: number;
  nb_a_envoyer: number;
  nb_envoyes_total: number;
  nb_echecs: number;
}

export interface AlerteRegle {
  id: number;
  libelle: string;
  visas_titres: string[] | string | null;
  motif_tous: string;
  motif_un_parmi: string;
  mode_declenchement: ModeDeclenchement;
  delai_jours: number;
  ecard_non_faite: number;
  note: string;
  type_marqueur: string | null;
  actif: number;
  derniere_evaluation: string | null;
  dernier_envoi: string | null;
  dernier_statut: StatutRegle;
  dernier_detail: string | null;
  date_creation?: string;
  date_maj?: string | null;
  compteurs?: AlerteRegleCompteurs;
}

export interface AlerteRegleInput {
  libelle: string;
  visas_titres: string[];
  motif_tous: string;
  motif_un_parmi: string;
  mode_declenchement: ModeDeclenchement;
  delai_jours: number;
  ecard_non_faite: boolean;
  note: string;
  actif: boolean;
}

export interface CandidatDossier {
  id: number;
  chemin: string | null;
  libelle_visa: string;
  date_depart: string;
  depart_iso: string | null;
  jours: number | null;
  /** Date ayant servi au J-n : 'arrivee_inde' uniquement pour la règle e-cards Inde (cf. TYPE_MARQUEUR_ARRIVEE_INDE) */
  date_reference?: 'depart' | 'arrivee_inde';
  date_ref?: string;
  etat_ecard: string;
  statut: StatutCandidat;
}

export interface Apercu {
  date_reference: string;
  dry_run: boolean;
  nb_concernes: number;
  nb_a_envoyer: number;
  nb_deja_alertes: number;
  nb_a_venir: number;
  nb_depasses: number;
  illisibles: { id: number; date_depart: string }[];
  candidats: CandidatDossier[];
  template: { code: string; present: boolean; actif: number };
}

export interface DernierePasse {
  date: string;
  declencheur: string;
  mode: 'reel' | 'dry_run';
  nb_regles: number;
  nb_dossiers_scannes: number;
  nb_candidats: number;
  nb_envoyes: number;
  nb_echecs: number;
  nb_template_inactif: number;
  duree_ms: number;
}

export interface EtatMoteur {
  dry_run: boolean;
  source_flag: string;
  tables_ok: boolean;
  raison_desactivation: string | null;
  template: { present: boolean; actif: number };
  // null quand l'horloge MySQL est illisible (MySQL injoignable)
  mysql_now: string | null;
  mysql_jour: string | null;
  tz_mysql: string | null;
  node_now: string | null;
  decalage_secondes: number | null;
  nb_regles_actives: number;
  passe_en_cours: boolean;
  derniere_passe: DernierePasse | null;
}

export interface JournalDossier {
  id_demande: number;
  chemin: string | null;
  libelle_visa: string | null;
  date_depart: string | null;
  message: string;
}

export interface JournalPasse {
  passe: string | null;
  statut: 'envoye' | 'echec';
  dossiers: JournalDossier[];
}

export interface Journal {
  nb_total: number;
  passes: JournalPasse[];
}

export interface VisaCatalogue {
  id: number;
  titre: string | null;
  libelle_pays: string;
  categorie: string | null;
}

// Normalise visas_titres (peut arriver en JSON string selon le driver) — même logique que parseVariables
export const parseTitres = (v: AlerteRegle['visas_titres']): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try {
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Mots-clés d'un CSV (miroir de motsCles côté moteur : minuscules, sans accents, ≥ 2 caractères)
export const motsCles = (s: string | null | undefined): string[] =>
  String(s || '')
    .split(',')
    .map((m) => m.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim())
    .filter((m) => m.length >= 2);

// Résumé lisible de la cible (miroir de resumeCible côté moteur)
export const resumerCible = (r: Pick<AlerteRegle, 'visas_titres' | 'motif_tous' | 'motif_un_parmi'>): string => {
  const parts: string[] = [];
  const t = parseTitres(r.visas_titres);
  if (t.length) parts.push(t.join(', '));
  const tous = motsCles(r.motif_tous);
  const un = motsCles(r.motif_un_parmi);
  if (tous.length || un.length) {
    parts.push(
      [tous.map((m) => `« ${m} »`).join(' et '), un.length ? `(${un.map((m) => `« ${m} »`).join(' ou ')})` : '']
        .filter(Boolean)
        .join(' et ')
    );
  }
  return parts.join(' ou ');
};

// Résumé du motif mots-clés seul, pour la Chip grise de la liste : « inde + (evisa | ecard) »
export const resumerMotif = (r: Pick<AlerteRegle, 'motif_tous' | 'motif_un_parmi'>): string => {
  const tous = motsCles(r.motif_tous);
  const un = motsCles(r.motif_un_parmi);
  return [tous.join(' + '), un.length ? `(${un.join(' | ')})` : ''].filter(Boolean).join(' + ');
};

// Règle e-cards Inde (codée en dur côté backend, alertesRegles/moteur.js) : J-n calculé sur la date d'arrivée
// en Inde du formulaire si elle est renseignée, sur la date de départ sinon.
export const TYPE_MARQUEUR_ARRIVEE_INDE = 'relance_ecard_j4';

// « Départ dans exactement 4 jours » / « Départ dans les 4 prochains jours »
// (règle e-cards Inde : « Arrivée en Inde (sinon départ) dans exactement 4 jours »)
export const texteDeclenchement = (r: Pick<AlerteRegle, 'mode_declenchement' | 'delai_jours'> & { type_marqueur?: string | null }): string => {
  const date = r.type_marqueur === TYPE_MARQUEUR_ARRIVEE_INDE ? 'Arrivée en Inde (sinon départ)' : 'Départ';
  return r.mode_declenchement === 'exact'
    ? `${date} dans exactement ${r.delai_jours} jour${Number(r.delai_jours) > 1 ? 's' : ''}`
    : `${date} dans les ${r.delai_jours} prochains jours`;
};

export const listerAlertesRegles = async (): Promise<AlerteRegle[]> =>
  (await axios.get<AlerteRegle[]>(`${BASE_URL}/alertes-regles`)).data;

export const etatMoteurAlertes = async (): Promise<EtatMoteur> =>
  (await axios.get<EtatMoteur>(`${BASE_URL}/alertes-regles/etat`)).data;

export const creerAlerteRegle = async (d: AlerteRegleInput): Promise<AlerteRegle> =>
  (await axios.post<AlerteRegle>(`${BASE_URL}/alertes-regles`, d)).data;

export const majAlerteRegle = async (id: number, d: Partial<AlerteRegleInput>): Promise<AlerteRegle> =>
  (await axios.put<AlerteRegle>(`${BASE_URL}/alertes-regles/${id}`, d)).data;

export const supprimerAlerteRegle = async (id: number): Promise<void> => {
  await axios.delete(`${BASE_URL}/alertes-regles/${id}`);
};

export const apercuAlerteRegle = async (
  d: Partial<AlerteRegleInput> & { id?: number; date_simulee?: string }
): Promise<Apercu> => (await axios.post<Apercu>(`${BASE_URL}/alertes-regles/apercu`, d)).data;

export const journalAlerteRegle = async (id: number): Promise<Journal> =>
  (await axios.get<Journal>(`${BASE_URL}/alertes-regles/${id}/journal`)).data;

export const lancerPasseAlertes = async (): Promise<DernierePasse> =>
  (await axios.post<DernierePasse>(`${BASE_URL}/alertes-regles/passe`, {})).data;

// Catalogue des visas (GET /visas, trié par titre côté serveur : re-trier par pays avant groupBy)
export const listerVisasCatalogue = async (): Promise<VisaCatalogue[]> =>
  (await axios.get<VisaCatalogue[]>(`${BASE_URL}/visas`)).data;
